import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Database, Globe, Package, Activity, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '../../../services/api';
import { CountryCard } from './CountryCard';
import { HealthScoreCircle } from './HealthBadge';

type VerifyStatus = 'idle' | 'fetching' | 'verifying' | 'complete' | 'error';

interface VerifyState {
  status: VerifyStatus;
  totalToVerify: number;
  currentCountry: string;
  countriesProcessed: number;
  totalCountries: number;
  verified: number;
  failed: number;
  skipped: number;
  error?: string;
}

export function CountryOverview() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [verifyState, setVerifyState] = useState<VerifyState>({
    status: 'idle',
    totalToVerify: 0,
    currentCountry: '',
    countriesProcessed: 0,
    totalCountries: 0,
    verified: 0,
    failed: 0,
    skipped: 0,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-store-browser-overview'],
    queryFn: () => api.getAdminStoreBrowserOverview(),
  });

  // Function to verify all slugs with progress tracking
  const handleVerifyAllSlugs = async () => {
    try {
      // Step 1: Get counts
      setVerifyState({
        status: 'fetching',
        totalToVerify: 0,
        currentCountry: '',
        countriesProcessed: 0,
        totalCountries: 0,
        verified: 0,
        failed: 0,
        skipped: 0,
      });

      const counts = await api.getSlugsToVerify();
      const countries = Object.keys(counts.byCountry);

      if (counts.total === 0) {
        setVerifyState((prev) => ({
          ...prev,
          status: 'complete',
          error: 'No stores with slugs to verify',
        }));
        return;
      }

      setVerifyState((prev) => ({
        ...prev,
        status: 'verifying',
        totalToVerify: counts.total,
        totalCountries: countries.length,
      }));

      // Step 2: Verify each country one by one
      let totalVerified = 0;
      let totalFailed = 0;
      let totalSkipped = 0;

      for (let i = 0; i < countries.length; i++) {
        const country = countries[i];

        setVerifyState((prev) => ({
          ...prev,
          currentCountry: country,
          countriesProcessed: i,
        }));

        try {
          const result = await api.verifyAllSlugs(country);
          totalVerified += result.verified;
          totalFailed += result.failed;
          totalSkipped += result.skipped;

          setVerifyState((prev) => ({
            ...prev,
            verified: totalVerified,
            failed: totalFailed,
            skipped: totalSkipped,
            countriesProcessed: i + 1,
          }));
        } catch (err) {
          // Continue to next country even if one fails
          console.error(`Failed to verify ${country}:`, err);
          totalSkipped += counts.byCountry[country];
          setVerifyState((prev) => ({
            ...prev,
            skipped: totalSkipped,
            countriesProcessed: i + 1,
          }));
        }
      }

      // Step 3: Complete
      setVerifyState((prev) => ({
        ...prev,
        status: 'complete',
        currentCountry: '',
      }));

      // Refresh the overview data
      queryClient.invalidateQueries({ queryKey: ['admin-store-browser-overview'] });
    } catch (err) {
      setVerifyState((prev) => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'Verification failed',
      }));
    }
  };

  const resetVerifyState = () => {
    setVerifyState({
      status: 'idle',
      totalToVerify: 0,
      currentCountry: '',
      countriesProcessed: 0,
      totalCountries: 0,
      verified: 0,
      failed: 0,
      skipped: 0,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Loading skeleton for stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 animate-pulse">
              <div className="h-12 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
        {/* Loading skeleton for cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-32 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load store browser data</p>
        <p className="text-sm text-gray-500 mt-2">Please try refreshing the page</p>
      </div>
    );
  }

  if (!data) return null;

  // Calculate totals from all sources
  const totalLastChance = data.countries.reduce((sum, c) => sum + (c.last_chance_count || 0), 0);
  const totalFamilyOffers = data.countries.reduce((sum, c) => sum + (c.family_offers_count || 0), 0);
  const totalLimitedTime = data.countries.reduce((sum, c) => sum + (c.limited_time_count || 0), 0);
  const totalPhysical = data.countries.reduce((sum, c) => sum + (c.physical_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Countries</p>
              <p className="text-2xl font-semibold">{data.countries.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Database className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Stores</p>
              <p className="text-2xl font-semibold">{data.total_stores.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Package className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-2xl font-semibold">{data.total_products.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Activity className="text-amber-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Global Health</p>
              <div className="flex items-center gap-2">
                <HealthScoreCircle score={data.global_health_score} size={36} strokeWidth={3} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Verify All Slugs Section */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700">Slug Verification</h3>
            <p className="text-xs text-gray-500">Verify all store slugs against IKEA pages</p>
          </div>

          {verifyState.status === 'idle' && (
            <button
              onClick={handleVerifyAllSlugs}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <CheckCircle size={16} />
              Verify All Slugs
            </button>
          )}

          {verifyState.status === 'complete' && (
            <button
              onClick={resetVerifyState}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <RefreshCw size={16} />
              Run Again
            </button>
          )}
        </div>

        {/* Progress Section */}
        {(verifyState.status === 'fetching' || verifyState.status === 'verifying') && (
          <div className="mt-4 space-y-3">
            {/* Progress Bar */}
            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
              {verifyState.status === 'fetching' ? (
                <div className="absolute inset-0 bg-primary-200">
                  <div className="h-full w-1/3 bg-primary-500 animate-pulse rounded-full" />
                </div>
              ) : (
                <div
                  className="h-full bg-primary-500 transition-all duration-300 rounded-full"
                  style={{
                    width: `${verifyState.totalCountries > 0 ? (verifyState.countriesProcessed / verifyState.totalCountries) * 100 : 0}%`
                  }}
                />
              )}
            </div>

            {/* Progress Text */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <RefreshCw size={14} className="animate-spin" />
                {verifyState.status === 'fetching' ? (
                  <span>Loading store counts...</span>
                ) : (
                  <span>
                    Verifying {verifyState.currentCountry}...
                    ({verifyState.countriesProcessed}/{verifyState.totalCountries} countries)
                  </span>
                )}
              </div>
              {verifyState.status === 'verifying' && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-green-600">{verifyState.verified} verified</span>
                  <span className="text-red-600">{verifyState.failed} failed</span>
                  {verifyState.skipped > 0 && (
                    <span className="text-gray-500">{verifyState.skipped} skipped</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Complete State */}
        {verifyState.status === 'complete' && (
          <div className="mt-4 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle size={16} />
              <span className="font-medium">{verifyState.verified}</span>
              <span>verified</span>
            </div>
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle size={16} />
              <span className="font-medium">{verifyState.failed}</span>
              <span>failed</span>
            </div>
            {verifyState.skipped > 0 && (
              <div className="flex items-center gap-2 text-gray-500">
                <span className="font-medium">{verifyState.skipped}</span>
                <span>skipped</span>
              </div>
            )}
          </div>
        )}

        {/* Error State */}
        {verifyState.status === 'error' && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle size={16} />
              <span>{verifyState.error || 'Verification failed'}</span>
            </div>
            <button
              onClick={resetVerifyState}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {/* Summary by Source */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Data Sources Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-semibold text-blue-600">{data.total_products.toLocaleString()}</p>
            <p className="text-sm text-blue-700">As-Is</p>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <p className="text-2xl font-semibold text-orange-600">{totalLastChance.toLocaleString()}</p>
            <p className="text-sm text-orange-700">Last Chance</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-semibold text-green-600">{totalFamilyOffers.toLocaleString()}</p>
            <p className="text-sm text-green-700">Family Offers</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-semibold text-red-600">{totalLimitedTime.toLocaleString()}</p>
            <p className="text-sm text-red-700">Limited Time</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-semibold text-purple-600">{totalPhysical.toLocaleString()}</p>
            <p className="text-sm text-purple-700">Physical</p>
          </div>
        </div>
      </div>

      {/* Country Cards */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Countries ({data.countries.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.countries
            .sort((a, b) => b.store_count - a.store_count)
            .map((country) => (
              <CountryCard
                key={country.code}
                code={country.code}
                name={country.name}
                storeCount={country.store_count}
                productCount={country.product_count}
                lastChanceCount={country.last_chance_count || 0}
                physicalCount={country.physical_count || 0}
                healthScore={country.health_score}
                healthStatus={country.health_status}
                lastUpdated={country.last_updated}
                metrics={country.metrics}
                onClick={() => navigate(`/admin/store-browser/${country.code}`)}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
